import React, { useEffect, useMemo, useState } from 'react';
import { RouteComponentProps, withRouter } from 'react-router';
import OrdersTab from './OrdersTab';
import RoutePlanTab from './RoutePlanTab';
import WarehouseTab from './WarehouseTab';
import ForecastTab from './ForecastTab';
import { AdminPageShell, TabBar } from '../../../components/admin/Shared';
import { axiosKK } from '../../../services/networkRequest';
import {
  PartnerUserAccess,
  PartnerUserAccessListResponse,
} from '../../../models/APIModels';

type Tab = 'orders' | 'route-plan' | 'warehouse' | 'forecast';

const TABS: { id: Tab; label: string; path: string }[] = [
  { id: 'orders', label: 'Orders', path: '/admin/logistics' },
  { id: 'route-plan', label: 'Route Plan', path: '/admin/logistics/route-plan' },
  { id: 'warehouse', label: 'Warehouse', path: '/admin/logistics/warehouse' },
  { id: 'forecast', label: 'Forecast', path: '/admin/logistics/forecast' },
];

// Legacy paths resolve to the same tabs so existing bookmarks keep working.
const LEGACY_PATHS: Record<string, Tab> = {
  '/admin/route-plan': 'route-plan',
  '/admin/warehouse': 'warehouse',
  '/admin/warehouse-forecast': 'forecast',
};

function resolveTab(pathname: string): Tab {
  if (LEGACY_PATHS[pathname]) return LEGACY_PATHS[pathname];
  const match = TABS.find((t) => pathname === t.path);
  return match ? match.id : 'orders';
}

function readPartnerIdFromSearch(search: string): number | null {
  const raw = new URLSearchParams(search).get('partnerId');
  if (!raw) return null;
  const n = Number(raw);
  return Number.isFinite(n) && n > 0 ? n : null;
}

function buildSearch(partnerId: number | null): string {
  if (partnerId == null) return '';
  return `?partnerId=${partnerId}`;
}

function LogisticsPage({ history, location }: RouteComponentProps) {
  const active: Tab = resolveTab(location.pathname);
  const partnerId = readPartnerIdFromSearch(location.search);

  const [partnerOptions, setPartnerOptions] = useState<{ id: number; name: string }[]>([]);
  const [partnersLoading, setPartnersLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    axiosKK
      .get<PartnerUserAccessListResponse>('/logistics/admin/users/access')
      .then(({ data }) => {
        if (cancelled) return;
        const map = new Map<number, string>();
        (data.users || []).forEach((u: PartnerUserAccess) => {
          if (u.partnerId && u.partnerName) {
            map.set(u.partnerId, u.partnerName);
          }
        });
        const opts = Array.from(map, ([id, name]) => ({ id, name })).sort(
          (a, b) => a.name.localeCompare(b.name)
        );
        setPartnerOptions(opts);
      })
      .catch(() => {
        // Silent — picker just stays empty, tabs show "select a partner" state.
      })
      .finally(() => {
        if (!cancelled) setPartnersLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const setPartnerId = (next: number | null) => {
    history.push({ pathname: location.pathname, search: buildSearch(next) });
  };

  const goTab = (path: string) => {
    history.push({ pathname: path, search: buildSearch(partnerId) });
  };

  const renderTab = () => {
    switch (active) {
      case 'orders':
        return <OrdersTab partnerId={partnerId} />;
      case 'route-plan':
        return <RoutePlanTab partnerId={partnerId} />;
      case 'warehouse':
        return <WarehouseTab partnerId={partnerId} />;
      case 'forecast':
        return <ForecastTab partnerId={partnerId} />;
    }
  };

  const partnerSelectLabel = useMemo(() => {
    if (partnersLoading) return 'Loading…';
    if (partnerOptions.length === 0) return 'No partners available';
    return undefined;
  }, [partnersLoading, partnerOptions.length]);

  return (
    <AdminPageShell
      title="Logistics"
      onBack={() => history.push('/admin/dashboard')}
      contextBar={
        <div className="admin-partner-picker">
          <label
            className="admin-partner-picker__label"
            htmlFor="logistics-partner-picker"
          >
            Partner
          </label>
          <select
            id="logistics-partner-picker"
            className="admin-partner-picker__select admin-select"
            value={partnerId ?? ''}
            onChange={(e) =>
              setPartnerId(e.target.value ? Number(e.target.value) : null)
            }
            disabled={partnersLoading || partnerOptions.length === 0}
          >
            <option value="">
              {partnerSelectLabel ?? 'Select a partner…'}
            </option>
            {partnerOptions.map((p) => (
              <option key={p.id} value={p.id}>
                {p.name}
              </option>
            ))}
          </select>
        </div>
      }
      tabs={
        <TabBar<Tab>
          items={TABS.map((t) => ({ id: t.id, label: t.label }))}
          activeId={active}
          onChange={(id) => {
            const next = TABS.find((t) => t.id === id);
            if (next) goTab(next.path);
          }}
          ariaLabel="Logistics sections"
        />
      }
    >
      {renderTab()}
    </AdminPageShell>
  );
}

export default withRouter(LogisticsPage);
